import { useEffect, useState } from "react";
import { toast } from "sonner";
import { WifiOff, Download, Trash2, CheckCircle2 } from "lucide-react";
import { tileUrlsForBounds, cacheTiles, cacheStats, clearTileCache, dataCacheCount, MAX_TILES } from "@/lib/offlineTiles";

export function OfflinePanel({ viewBounds, zoom, onClose, prefetch }) {
  const [stats, setStats] = useState({ count: 0, supported: true });
  const [dataCount, setDataCount] = useState(0);
  const [progress, setProgress] = useState(null);
  const [online, setOnline] = useState(navigator.onLine);

  const refresh = () => { cacheStats().then(setStats); dataCacheCount().then(setDataCount); };
  useEffect(() => {
    refresh();
    const up = () => setOnline(true), down = () => setOnline(false);
    window.addEventListener("online", up); window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);

  const zMin = Math.max(10, Math.min(14, Math.round(zoom ?? 12)));
  const zMax = Math.min(16, zMin + 3);
  const urls = viewBounds ? tileUrlsForBounds(viewBounds, zMin, zMax) : [];
  const tooMany = urls.length > MAX_TILES;

  const start = async () => {
    if (!viewBounds || tooMany) return;
    setProgress({ done: 0, total: urls.length, failed: 0 });
    const result = await cacheTiles([...urls], (done, failed) => setProgress({ done, total: urls.length, failed }));
    let dataSaved = 0;
    try { dataSaved = await prefetch?.(); } catch { /* best effort */ }
    setProgress(null); refresh();
    toast.success(`${result.done - result.failed} tiles + ${dataSaved} data files saved for offline use${result.failed ? ` (${result.failed} tiles failed)` : ""}`);
  };
  const clear = async () => { await clearTileCache(); refresh(); toast.success("Offline tiles cleared"); };

  return (
    <div data-testid="offline-panel" className="glass rounded-2xl p-4 w-80 shadow-2xl fade-up text-sm">
      <div className="flex items-center justify-between mb-2"><p className="data-label flex items-center gap-2"><WifiOff size={12} /> Offline basemap</p><button type="button" onClick={onClose} className="text-xs text-muted-foreground hover:text-foreground">Close</button></div>
      <p className="text-xs text-muted-foreground">Saves OpenStreetMap tiles for the <b>area currently on screen</b> (zoom {zMin}–{zMax}) plus the current parcels, uploaded layers and your field notes, so the map keeps working with patchy signal. New notes still need a connection to save.</p>
      <div className="mt-3 rounded-xl bg-muted/70 p-3 text-xs flex items-center justify-between">
        <span data-testid="offline-cache-count" className="font-mono">{stats.count} tiles · {dataCount} data files</span>
        <span className={`flex items-center gap-1 ${online ? "text-emerald-700" : "text-amber-700"}`}>{online ? <CheckCircle2 size={12} /> : <WifiOff size={12} />}{online ? "online" : "offline mode"}</span>
      </div>
      {progress ? (
        <div className="mt-3">
          <div className="h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-emerald-600 transition-[width]" style={{ width: `${(progress.done / progress.total) * 100}%` }} /></div>
          <p data-testid="offline-progress" className="mt-1 text-xs font-mono text-muted-foreground">{progress.done}/{progress.total} tiles{progress.failed ? ` · ${progress.failed} failed` : ""}</p>
        </div>
      ) : (
        <div className="mt-3 flex gap-2">
          <button type="button" data-testid="offline-download-button" onClick={start} disabled={!viewBounds || tooMany || !online || !stats.supported}
            className="flex-1 h-9 rounded-full bg-emerald-900 text-white dark:bg-emerald-500 dark:text-slate-950 text-xs font-semibold flex items-center justify-center gap-1.5 disabled:opacity-50">
            <Download size={14} /> {tooMany ? `Zoom in (${urls.length} tiles > ${MAX_TILES})` : `Save ${urls.length} tiles`}
          </button>
          <button type="button" data-testid="offline-clear-button" onClick={clear} disabled={!stats.count} className="h-9 px-3 rounded-full border text-xs flex items-center gap-1 hover:bg-muted disabled:opacity-40"><Trash2 size={14} /> Clear</button>
        </div>
      )}
      {!stats.supported && <p className="mt-2 text-xs text-amber-700">This browser doesn't support offline caching.</p>}
    </div>
  );
}
